import { lineErrorText } from './lineErrors';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Contact, type Inquiry, type Offer, type OfferDefaults } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { ContactPicker } from '../../components/ContactPicker';
import { draftFromLine, emptyLine, LinesEditor, linesBody, linesProblem, useUnits, type LineDraft } from './LinesEditor';
import { RecipientCard } from './RecipientCard';
import { bpText, centsText, parseBp } from '../../money';
import './offers.css';

/**
 * Angebot anlegen oder einen Entwurf bearbeiten (KON-098). Positionen kommen aus den Produkten (KON-113) oder werden frei geschrieben, Preise lassen sich anpassen.
 * Die Summen rechnet dieselbe Funktion wie der Server (offerTotals), sie stehen beim Tippen schon richtig da. Aus einer Anfrage heraus (?anfrage=…) ist der
 * Kontakt vorbelegt und eine Online-Schätzung steht als erste Position da (KON-103).
 */
export function OfferEditorPage() {
  const { id } = useParams();
  const isCreate = !id;
  const [params] = useSearchParams();
  const inquiryId = isCreate ? params.get('anfrage') : null;
  const contactParam = isCreate ? params.get('kontakt') : null;
  const navigate = useNavigate();
  const client = useQueryClient();

  const existing = useQuery({ queryKey: ['offer', id], queryFn: async () => (await api<{ data: Offer }>('GET', `/offers/${id}`)).data, enabled: !isCreate });
  const defaults = useQuery({ queryKey: ['offer-defaults'], queryFn: async () => (await api<{ data: OfferDefaults }>('GET', '/offers/defaults')).data, enabled: isCreate });
  const inquiry = useQuery({ queryKey: ['inquiry', inquiryId], queryFn: async () => (await api<{ data: Inquiry }>('GET', `/inquiries/${inquiryId}`)).data, enabled: !!inquiryId });
  const { units, symbols: unitSymbols } = useUnits();

  const [contact, setContact] = useState<{ id: string; name: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [introText, setIntroText] = useState('');
  const [closingText, setClosingText] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [discount, setDiscount] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const loaded = useRef(false);

  // Vorbelegen: bei einem Entwurf aus dem Angebot, sonst aus den Vorgaben und gegebenenfalls aus der Anfrage
  useEffect(() => {
    if (loaded.current) return;
    if (!isCreate && existing.data) {
      const o = existing.data;
      loaded.current = true;
      setContact({ id: o.contactId, name: o.contactName });
      setSubject(o.subject);
      setIntroText(o.introText ?? '');
      setClosingText(o.closingText ?? '');
      setValidUntil(o.validUntil ?? '');
      setDiscount(bpText(o.discountBp));
      setLines(o.lines.map(draftFromLine));
    } else if (isCreate && defaults.data && (!inquiryId || inquiry.data || inquiry.isError)) {
      loaded.current = true;
      setIntroText(defaults.data.introText);
      setClosingText(defaults.data.closingText);
      setValidUntil(defaults.data.validUntil);
      const i = inquiry.data;
      if (i) {
        setSubject(`Angebot zu Ihrer Anfrage ${i.reference}`);
        const estimate = Number(i.fields.estimate);
        if (Number.isFinite(estimate) && estimate > 0) {
          setLines([{ ...emptyLine('pauschal'), title: 'Leistung laut Online-Schätzung', price: centsText(Math.round(estimate * 100)) }]);
        } else setLines([emptyLine()]);
      } else setLines([emptyLine()]);
    }
  }, [isCreate, existing.data, defaults.data, inquiry.data, inquiry.isError, inquiryId]);

  // Kontakt der Anfrage oder aus ?kontakt=, sobald bekannt
  const presetContactId = inquiry.data?.contactId ?? contactParam;
  const inquiryContact = useQuery({
    queryKey: ['contact', presetContactId],
    queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${presetContactId}`)).data,
    enabled: !!presetContactId && !contact,
  });
  useEffect(() => {
    if (inquiryContact.data && !contact) setContact({ id: inquiryContact.data.id, name: inquiryContact.data.name });
  }, [inquiryContact.data, contact]);

  const discountBp = parseBp(discount || '0');

  const save = useMutation({
    mutationFn: () => {
      const body = {
        contactId: contact?.id,
        subject: subject.trim(),
        introText: introText.trim(),
        closingText: closingText.trim(),
        validUntil,
        discountBp: discountBp ?? 0,
        lines: linesBody(lines),
      };
      return isCreate
        ? api<{ data: Offer }>('POST', '/offers', { ...body, ...(inquiryId ? { inquiryId } : {}), validUntil: validUntil || undefined })
        : api<{ data: Offer }>('PATCH', `/offers/${id}`, { ...body, validUntil: validUntil || null });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['offers'] });
      client.setQueryData(['offer', res.data.id], res.data);
      navigate(`/angebote/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError(lineErrorText(e.fields) ?? 'Bitte prüfen Sie Ihre Eingaben.');
      } else setFormError(e instanceof ApiError ? e.message : 'Das Angebot konnte nicht gespeichert werden.');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    if (!contact) {
      setErrors({ contactId: 'Bitte einen Empfänger wählen.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      return;
    }
    const problem = linesProblem(lines, units ? unitSymbols : null);
    if (problem) {
      setFormError(problem);
      return;
    }
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && existing.data && existing.data.status !== 'draft') {
    return (
      <div className="card narrow">
        <h1>Nicht mehr änderbar</h1>
        <p>Dieses Angebot ist abgeschlossen. Für Änderungen legen Sie eine neue Fassung an.</p>
        <Link to={`/angebote/${id}`}>Zum Angebot</Link>
      </div>
    );
  }
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const smallBusiness = (isCreate ? defaults.data?.smallBusiness : existing.data?.smallBusiness) ?? false;
  const needsContact = !!inquiry.data && !inquiry.data.contactId;

  return (
    <>
      <p><Link to={isCreate ? (inquiryId ? `/anfragen/${inquiryId}` : contactParam ? `/kontakte/${contactParam}` : '/angebote') : `/angebote/${id}`} className="back-link">← {isCreate ? (inquiryId ? 'Zur Anfrage' : contactParam ? 'Zum Kontakt' : 'Alle Angebote') : 'Zum Angebot'}</Link></p>
      <div className="page-head"><h1>{isCreate ? 'Neues Angebot' : 'Entwurf bearbeiten'}</h1></div>
      {inquiry.data && <p className="muted">Zur Anfrage {inquiry.data.reference} von {inquiry.data.contact.name}.</p>}
      {needsContact && <div className="alert alert--warn">Der Anfrage ist noch kein Kontakt zugeordnet. Wählen Sie unten den Empfänger oder ordnen Sie in der Anfrage zuerst einen Kontakt zu.</div>}

      <form className="card offer-editor" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <div className="field">
            <span className="field__label">Empfänger <abbr title="Pflichtfeld">*</abbr></span>
            <ContactPicker value={contact} onChange={setContact} invalid={!!errors.contactId} label="Empfänger" hint={<>Neue Kunden legen Sie unter <Link to="/kontakte/neu">Kontakte</Link> an.</>} />
            {contact && <RecipientCard contactId={contact.id} />}
            {error('contactId')}
          </div>
          <label className="field">
            <span>Betreff <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} aria-invalid={!!errors.subject} />
            {error('subject')}
          </label>
          <label className="field">
            <span>Gültig bis</span>
            <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} aria-invalid={!!errors.validUntil} />
            {error('validUntil')}
          </label>
        </div>
        <label className="field">
          <span>Einleitung</span>
          <textarea className="input textarea textarea--short" value={introText} onChange={(e) => setIntroText(e.target.value)} maxLength={5000} />
        </label>

        <h2 className="offer-editor__h">Positionen</h2>
        <LinesEditor lines={lines} setLines={setLines} discount={discount} setDiscount={setDiscount} smallBusiness={smallBusiness} fixUnits={isCreate} />

        <label className="field">
          <span>Schlusstext</span>
          <textarea className="input textarea textarea--short" value={closingText} onChange={(e) => setClosingText(e.target.value)} maxLength={5000} />
        </label>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Entwurf anlegen' : 'Entwurf speichern'}</button>
          <Link className="btn btn--secondary" to={isCreate ? '/angebote' : `/angebote/${id}`}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
